/**
 * Normalizes a populated participant document or a raw id to a comparable string.
 * @param {string|{ _id?: string }} v
 * @returns {string}
 */
export function participantIdString(v) {
  if (v == null) return "";
  if (typeof v === "object" && v && "_id" in v && v._id != null) {
    return String(v._id);
  }
  return String(v);
}

/**
 * "Served by" column: AI (from ref) plus human participants, de-duplicated when the bot also appears in agentParticipants.
 * @param {{ aiAssistant?: object, agentParticipants?: object[] } | null | undefined} currentConversation
 * @returns {string}
 */
export function formatServedByFromConversation(currentConversation) {
  if (!currentConversation) return "—";

  const parts = [];
  const ai = currentConversation.aiAssistant;
  const aiId = participantIdString(ai?._id);

  if (ai) {
    const name = (ai.name && String(ai.name).trim()) || "Assistant";
    parts.push(name);
  }

  for (const p of currentConversation.agentParticipants || []) {
    const pid = participantIdString(p);
    if (aiId && pid && pid === aiId) {
      continue;
    }
    const label = (p?.sudoName || p?.name || "").trim() || "—";
    parts.push(label);
  }

  return parts.length ? parts.join(", ") : "—";
}

/**
 * Resolves a display name for a chat row (AI bot, team directory user, or conversation participant).
 * @param {{
 *   message: { senderID?: string, name?: string },
 *   currentConversation: { aiAssistant?: { _id?: string, name?: string } | null, agentParticipants?: Array<{ _id?: string, sudoName?: string, name?: string }> } | null,
 *   overAllUsers: Array<{ _id?: string, sudoName?: string }> | undefined
 * }} p
 * @returns {string}
 */
export function resolveMessageSenderName({ message, currentConversation, overAllUsers = [] }) {
  const senderID = message?.senderID;
  const sid = participantIdString(senderID);
  const ai = currentConversation?.aiAssistant;
  const aiId = participantIdString(ai?._id);
  const senderTag = String(message?.sender || "").toLowerCase();

  if (aiId && sid && sid === aiId) {
    return (ai?.name && String(ai.name).trim()) || message?.name || "Assistant";
  }

  if (senderTag === "agent" || senderTag === "assistant" || senderTag === "bot") {
    return (ai?.name && String(ai.name).trim()) || (message?.name && String(message.name).trim()) || "Assistant";
  }

  const fromDirectory = overAllUsers.find((u) => String(u?._id) === sid);
  if (fromDirectory?.sudoName) {
    return fromDirectory.sudoName;
  }

  const p = (currentConversation?.agentParticipants || []).find(
    (x) => participantIdString(x) === sid
  );
  const fromParticipant = p?.sudoName || p?.name;
  if (fromParticipant) {
    return fromParticipant;
  }

  return message?.name || message?.senderID || "";
}

/**
 * Normalizes the server `agentJoinedChat` payload into the shape used in the local messages list.
 * @param {{ currentMessage?: object }} data
 * @returns {object | null}
 */
export function messageRowFromAgentJoinedEvent(data) {
  const m = data?.currentMessage;
  if (!m) {
    return null;
  }
  return {
    file: m.file,
    message: m.message,
    name: m.senderID,
    senderID: m.senderID,
    audio: m.audio,
    to: m.to,
    time: m.time,
    type: m.type,
  };
}
